import React, { useEffect, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Save, RotateCcw, Send, ChevronDown, AlertTriangle, Eye } from "lucide-react";

const LANGS = ["fr", "en", "es", "pt"];
const LANG_FLAGS = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", pt: "🇵🇹" };
const LANG_LABELS = { fr: "Français", en: "English", es: "Español", pt: "Português" };

// Mirrors backend _EMAIL_SAMPLE_VARS so the preview matches a real send.
const SAMPLE_VARS = {
  name: "Marie",
  subject: "Problème de connexion",
  reply: "Bonjour, nous avons réinitialisé votre accès. Réessayez et dites-nous si le souci persiste.",
  church_name: "Église La Grâce",
  host_name: "Pasteur Jean",
  live_title: "Culte du dimanche",
  content: "<ul><li><b>3</b> notification(s)</li><li><b>2</b> nouvel(aux) abonné(s)</li><li><b>18</b> nouvelle(s) publication(s)</li></ul>",
};

const fillVars = (s) => (s || "").replace(/\{(\w+)\}/g, (m, k) => (k in SAMPLE_VARS ? SAMPLE_VARS[k] : m));

// Mirrors backend emailer._wrap so the admin sees the exact rendered email.
const wrapHtml = (title, body, footerText) => {
  const footer = (footerText || "").trim() || "Vous recevez cet e-mail car vous avez un compte Yalola.";
  return `<div style="font-family:Arial,Helvetica,sans-serif;background:#FAF8F5;padding:24px">
  <div style="max-width:560px;margin:0 auto;background:#ffffff;border-radius:16px;padding:28px;border:1px solid #eeeeee">
    <div style="text-align:center;margin-bottom:10px"><img src="https://static.prod-images.emergentagent.com/jobs/3eb3e265-2f1e-4e99-9ffb-e30fc4500b04/images/5780229aff401ebeccabed7952ccfe05e8a2390f7352cb22f092b2b0606ee6da.jpeg" alt="Yalola" width="56" height="56" style="border-radius:14px;display:inline-block" /></div>
    <div style="font-size:20px;font-weight:bold;color:#D97757;margin-bottom:4px;text-align:center">Yalola</div>
    <h2 style="margin:12px 0;color:#1F2937;font-size:18px">${fillVars(title)}</h2>
    <div style="color:#374151;font-size:14px;line-height:1.6">${fillVars(body)}</div>
    <p style="color:#9CA3AF;font-size:12px;margin-top:24px">${footer}</p>
  </div>
</div>`;
};

// Effective content with the same fallback chain as the backend: lang → en → fr(default).
const effective = (translations, def, lang, field) => {
  for (const L of [lang, "en", "fr"]) {
    const v = translations?.[L]?.[field];
    if (v) return v;
  }
  return (def || {})[field] || "";
};

export const EmailsPanel = ({ isSuper }) => {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ email_enabled: true, from_email: "", from_name: "" });
  const [drafts, setDrafts] = useState({});
  const [footer, setFooter] = useState("");
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [busy, setBusy] = useState("");
  const [lang, setLang] = useState("fr");
  const [showPreview, setShowPreview] = useState({});

  const hydrate = (list) => {
    const d = {};
    (list || []).forEach((t) => { d[t.key] = { enabled: t.enabled, translations: JSON.parse(JSON.stringify(t.translations || {})) }; });
    setDrafts(d);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data.templates || []);
      setMeta({ email_enabled: data.email_enabled, from_email: data.from_email, from_name: data.from_name });
      setFooter(data.footer_text || "");
      hydrate(data.templates || []);
    } catch { toast.error("Erreur de chargement des e-mails"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const upd = (key, patch) => setDrafts((s) => ({
    ...s,
    [key]: { ...s[key], translations: { ...s[key].translations, [lang]: { ...(s[key].translations?.[lang] || {}), ...patch } } },
  }));
  const setEnabledDraft = (key, enabled) => setDrafts((s) => ({ ...s, [key]: { ...s[key], enabled } }));

  const replaceTemplate = (t) => {
    setTemplates((ts) => ts.map((x) => (x.key === t.key ? t : x)));
    setDrafts((s) => ({ ...s, [t.key]: { enabled: t.enabled, translations: JSON.parse(JSON.stringify(t.translations || {})) } }));
  };

  const save = async (key) => {
    setBusy(`${key}:save`);
    try {
      const tr = drafts[key].translations?.[lang] || {};
      const { data } = await api.patch(`/admin/email-templates/${key}`, { lang, subject: tr.subject || "", title: tr.title || "", body: tr.body || "" });
      replaceTemplate(data.template);
      toast.success(`Modèle enregistré (${LANG_LABELS[lang]})`);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur — réservé au super admin"); }
    setBusy("");
  };
  const toggleEnabled = async (key, enabled) => {
    setEnabledDraft(key, enabled);
    try { const { data } = await api.patch(`/admin/email-templates/${key}`, { enabled }); replaceTemplate(data.template); }
    catch (e) { setEnabledDraft(key, !enabled); toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const reset = async (key) => {
    if (!window.confirm(`Réinitialiser ce modèle (${LANG_LABELS[lang]}) ?`)) return;
    setBusy(`${key}:reset`);
    try { const { data } = await api.post(`/admin/email-templates/${key}/reset`, { lang }); replaceTemplate(data.template); toast.success("Modèle réinitialisé"); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    setBusy("");
  };
  const test = async (key) => {
    setBusy(`${key}:test`);
    try { const { data } = await api.post(`/admin/email-templates/${key}/test`, { lang }); toast.success(`E-mail de test (${lang.toUpperCase()}) envoyé à ${data.to}`); }
    catch (e) { toast.error(e?.friendlyMessage || "Envoi impossible — vérifie Resend dans Intégrations"); }
    setBusy("");
  };
  const saveFooter = async () => {
    setBusy("footer");
    try { await api.patch("/admin/email-appearance", { footer_text: footer }); toast.success("Pied de page enregistré"); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    setBusy("");
  };

  if (loading) return <AdminSkeleton />;

  return (
    <div className="space-y-4" data-testid="email-panel">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Mail size={20} /></div>
        <div>
          <h2 className="text-lg font-bold font-display">E-mails aux utilisateurs</h2>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Modifie l'objet, le titre et le contenu des e-mails automatiques, dans chaque langue. Les variables entre accolades (ex. <code>{"{name}"}</code>) sont remplacées à l'envoi. L'e-mail part dans la langue du destinataire (sinon anglais, sinon français).</p>
        </div>
      </div>

      {/* Language selector */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="email-lang-tabs">
        <span className="text-xs font-medium mr-1" style={{ color: "var(--yalola-text-muted)" }}>Langue :</span>
        {LANGS.map((L) => (
          <button
            key={L}
            onClick={() => setLang(L)}
            className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
            style={lang === L
              ? { background: "var(--yalola-primary)", color: "#fff" }
              : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
            data-testid={`email-lang-tab-${L}`}
          >
            <span className="mr-1">{LANG_FLAGS[L]}</span> {LANG_LABELS[L]}
          </button>
        ))}
      </div>

      {!meta.email_enabled && (
        <div className="rounded-xl p-3 flex items-start gap-2 text-sm" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="email-not-configured">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>L'envoi d'e-mails n'est pas configuré. Renseigne la clé Resend dans <b>Intégrations / API</b> pour que ces e-mails partent réellement.</span>
        </div>
      )}

      {/* Global footer */}
      <div className="yalola-card p-4">
        <Label>Pied de page commun (bas de tous les e-mails)</Label>
        <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
          <Input value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="Vous recevez cet e-mail car vous avez un compte Yalola." disabled={!isSuper} data-testid="email-footer-input" />
          <Button onClick={saveFooter} disabled={!isSuper || busy === "footer"} className="pill pill-primary shrink-0" data-testid="email-footer-save"><Save size={15} className="mr-1.5" /> Enregistrer</Button>
        </div>
        {(meta.from_name || meta.from_email) && (
          <p className="text-xs mt-2" style={{ color: "var(--yalola-text-muted)" }}>Expéditeur : <b>{meta.from_name || "Yalola"}</b> &lt;{meta.from_email || "non défini"}&gt; — modifiable dans Intégrations / API.</p>
        )}
      </div>

      {/* Templates */}
      <div className="space-y-2.5">
        {templates.map((t) => {
          const d = drafts[t.key] || { translations: {} };
          const tr = d.translations?.[lang] || {};
          const open = openKey === t.key;
          const preview = !!showPreview[t.key];
          const previewSubject = effective(d.translations, t.default, lang, "subject");
          const previewTitle = effective(d.translations, t.default, lang, "title");
          const previewBody = effective(d.translations, t.default, lang, "body");
          const isTranslated = lang === "fr" || !!(tr.subject || tr.title || tr.body);
          return (
            <div key={t.key} className="yalola-card overflow-hidden" data-testid={`email-tmpl-${t.key}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setOpenKey(open ? null : t.key)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenKey(open ? null : t.key); } }}
                className="w-full flex items-center gap-3 p-4 text-left cursor-pointer"
                data-testid={`email-tmpl-header-${t.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{t.name}</span>
                    {!isTranslated && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>Non traduit ({lang.toUpperCase()})</span>}
                    {!d.enabled && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#B91C1C" }}>Désactivé</span>}
                  </div>
                  <p className="text-xs mt-0.5 truncate" style={{ color: "var(--yalola-text-muted)" }}>{t.description}</p>
                </div>
                <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                  <Switch checked={!!d.enabled} onCheckedChange={(v) => toggleEnabled(t.key, v)} disabled={!isSuper} data-testid={`email-tmpl-toggle-${t.key}`} />
                </div>
                <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "var(--yalola-text-muted)" }} />
              </div>

              {open && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: "var(--yalola-border)" }}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-3">
                    {/* Editor */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-xs font-medium" style={{ color: "var(--yalola-text-muted)" }}>Variables :</span>
                          {(t.vars || []).map((v) => (
                            <code key={v} className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-primary)" }}>{`{${v}}`}</code>
                          ))}
                        </div>
                        <Button variant="outline" size="sm" className="pill lg:hidden" onClick={() => setShowPreview((s) => ({ ...s, [t.key]: !preview }))} data-testid={`email-tmpl-preview-toggle-${t.key}`}>
                          <Eye size={14} className="mr-1.5" /> {preview ? "Masquer" : "Aperçu"}
                        </Button>
                      </div>
                      <div>
                        <Label>Objet <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>· {LANG_LABELS[lang]}</span></Label>
                        <Input value={tr.subject || ""} onChange={(e) => upd(t.key, { subject: e.target.value })} disabled={!isSuper} placeholder={lang !== "fr" ? t.default?.subject : ""} data-testid={`email-tmpl-subject-${t.key}`} />
                      </div>
                      <div>
                        <Label>Titre (en-tête de l'e-mail)</Label>
                        <Input value={tr.title || ""} onChange={(e) => upd(t.key, { title: e.target.value })} disabled={!isSuper} placeholder={lang !== "fr" ? t.default?.title : ""} data-testid={`email-tmpl-title-${t.key}`} />
                      </div>
                      <div>
                        <Label>Corps (HTML)</Label>
                        <Textarea rows={8} value={tr.body || ""} onChange={(e) => upd(t.key, { body: e.target.value })} disabled={!isSuper} className="font-mono text-xs" placeholder={lang !== "fr" ? t.default?.body : ""} data-testid={`email-tmpl-body-${t.key}`} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => save(t.key)} disabled={!isSuper || busy === `${t.key}:save`} className="pill pill-primary" data-testid={`email-tmpl-save-${t.key}`}><Save size={15} className="mr-1.5" /> Enregistrer</Button>
                        <Button onClick={() => reset(t.key)} variant="outline" disabled={!isSuper || busy === `${t.key}:reset`} className="pill" data-testid={`email-tmpl-reset-${t.key}`}><RotateCcw size={15} className="mr-1.5" /> Réinitialiser</Button>
                        <Button onClick={() => test(t.key)} variant="outline" disabled={busy === `${t.key}:test`} className="pill" data-testid={`email-tmpl-test-${t.key}`}><Send size={15} className="mr-1.5" /> Envoyer un test</Button>
                      </div>
                      {!isSuper && <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Seul le super administrateur peut modifier ces modèles.</p>}
                    </div>

                    {/* Live preview */}
                    <div className={`${preview ? "block" : "hidden"} lg:block`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Eye size={14} style={{ color: "var(--yalola-text-muted)" }} />
                        <span className="text-xs font-medium" style={{ color: "var(--yalola-text-muted)" }}>Aperçu — objet : <b style={{ color: "var(--yalola-text)" }}>{fillVars(previewSubject)}</b></span>
                      </div>
                      <iframe
                        title={`preview-${t.key}`}
                        srcDoc={wrapHtml(previewTitle, previewBody, footer)}
                        className="w-full rounded-lg border"
                        style={{ borderColor: "var(--yalola-border)", height: 400, background: "#FAF8F5" }}
                        sandbox=""
                        data-testid={`email-tmpl-preview-${t.key}`}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmailsPanel;
